import { startTransition, useState } from 'react';
import useSWR from 'swr';
import { useIntl } from 'react-intl';
import { useConfig, useDeviceType } from '@packages/shared';
import type { NavigationData } from './types';
import { getNavigationDataEndpoint } from './getNavigationDataEndpoint';

const fetcher = (endpoint: string) => (): Promise<NavigationData> =>
  fetch(endpoint).then((res) => {
    if (!res.ok) {
      // throw error when response status not ok -> error boundary catches and logs the error
      throw new Error(`Endpoint: ${res.url} - Status ${res.status}: ${res.statusText}`);
    }
    return res.json();
  });

/**
 * Fetch navigation data from the API
 *
 * @returns navigation data, initially only the first few levels ("sliced"), and a function to load the full data on-demand
 */
export const useNavigationData = (): {
  data: NavigationData;
  loadFullData: () => void;
} => {
  const [sliced, setSliced] = useState(true);
  const { locale } = useIntl();
  const { clientId } = useConfig();
  const { isDesktop } = useDeviceType();
  const cacheKey = `nav-data-${clientId}-${locale}-${isDesktop ? 'web' : 'mob'}-${sliced}`;

  const endpoint = getNavigationDataEndpoint(clientId, isDesktop ? 'web' : 'mob', locale, sliced);
  const { data } = useSWR<NavigationData>(cacheKey, fetcher(endpoint), {
    keepPreviousData: true,
    revalidateIfStale: true,
    revalidateOnMount: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const loadFullData = () => {
    startTransition(() => setSliced(false));
  };

  return { data: data || [], loadFullData };
};
